import {Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {CommonService} from '../../../../services/apiServices/business/common.service';

/**
 * Represent the upload file component
 */
@Component({
  selector: 'app-files-upload',
  templateUrl: './files-upload.component.html',
  styleUrls: ['./files-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FilesUploadComponent implements OnInit, OnChanges {

  /**
   * list of acceptedExtensions
   */
  @Input('acceptedFiles') public acceptedFiles: string[];

  /**
   * if the component should clear selected files
   */
  @Input('shouldClearFiles') public shouldClearFiles: Subject<any>;

  /**
   * Title of the component
   */
  @Input('filesTitle') public filesTitle: string;

  /**
   * If the component should handle multiple files select or just one
   */
  @Input('moreThanOneFile') public moreThanOneFile: boolean;

  /**
   * If the component should handle multiple files select or just one
   */
  @Input('optional') public optional: boolean;

  /**
   * If selected files match prerequisites
   */
  @Output() public isValid: EventEmitter<boolean>;

  /**
   * List of selected files for upload
   */
  @Output() public selectedFiles: EventEmitter<any[]>;

  /**
   * Permit direct handle of fileUpload.files
   * usefull for templating (need reference not value)
   */
  @ViewChild('fileUpload', { static: true } ) fileUpload;
  public displayDropZone = false;

  /**
   * If the component should disable the upload button (using external css due to PrimeNg gaps)
   */
  //public disableSelect: boolean;

  /**
   * Title of error Message display when the wrong kind of file is selected by user
   */
  private errorExtensionTitle;

  /**
   * Content of error Message display when the wrong kind of file is selected by user
   */
  private errorExtensionMessage;
  private errorSizeMessage;

  /**
   * Default constructor: Do Nothing
   * @param commonService
   * @param {TranslateService} translate - handle component translation
   */
  constructor(private commonService: CommonService, private translate: TranslateService) {
    this.isValid = new EventEmitter<boolean>();
    this.selectedFiles = new EventEmitter<any[]>();
  }

  /**
   * Init variables
   */
  public ngOnInit(): void {
    this.getTradForComponent();
    // this.disableSelect = false;
    this.isValid.emit(this.uploadIsValid());
    if (this.shouldClearFiles) {
      this.shouldClearFiles.subscribe(event => {
        this.clearFiles();
      });
    }
  }

  /**
   * update validty on change (if the user switch of upload kind)
   */
  public ngOnChanges(): void {
    // this.disableSelect = this.getNumberOfFiles() > 0 && !this.moreThanOneFile;
    this.isValid.emit(this.uploadIsValid());
  }

  /**
   * Format extensions array as a string that could be interpreted by fileUpload
   *
   * @return {string} the result like '.pdf,.txt,...,.xml'
   */
  public formatExtensionsForRestrictions(): string {
    let result = '';
    for (const extension of this.acceptedFiles) {
      if (result.length > 0) {
        result = result.concat(',');
      }
      result = result.concat(',.' + extension + ',.' + extension.toUpperCase());
    }
    return result;
  }  /**
   * Format extensions array for acceptable dnd
   *
   * @return {string} the result like '.pdf,.txt,...,.xml'
   */
  public formatDnDExtensionsForRestrictions(): string[] {
    const result = [];
    for (const extension of this.acceptedFiles) {
      result.push(extension);
      result.push(extension.toUpperCase());
    }
    return result;
  }

  /**
   * Format extensions array as a string that could be display for information
   *
   * @return {string} the result like 'pdf , txt , ... , xml'
   */
  public formatExtensionsForMessage(): string {
    let result = '';
    for (const extension of this.acceptedFiles) {
      result = (result.length <= 0 ? extension : result.concat(', ' + extension));
    }
    return result;
  }

  /**
   * determine if the file Array contain at least one file extension that is not allowed
   * @param {string[]} extensions - like ['.pdf','.csv','.txt']
   * @return {boolean} - true if there's a file not included into extensions
   */
  private fileListContainsANotAllowedExtension(): boolean {
    if (this.fileUpload && this.fileUpload.files) {
      let fileIsAlreadyInclude = false;

      for (const file of this.fileUpload.files) {
        const fileExt = file.name.substr(file.name.lastIndexOf('.') + 1);
        for (const extension of this.acceptedFiles) {
          if (extension.toUpperCase() === fileExt.toUpperCase()) {
            fileIsAlreadyInclude = true;
            break;
          }
        }

        if (!fileIsAlreadyInclude) {
          return true;
        }
      }

    }
    return false;
  }

  /**
   * handle files selections
   * @param {any} event - triggered by fileUpload
   */
  public handleAddingFiles(event: any): void {
    for (const file of event.files) {
      let fileTypeSupported = false;
      const fileExt = file.name.substr(file.name.lastIndexOf('.') + 1);
      for (const extension of this.acceptedFiles) {
        if (extension.toUpperCase() === fileExt.toUpperCase()) {
          fileTypeSupported = true;
          break;
        }
      }
      if (!fileTypeSupported) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: this.errorExtensionTitle,
          detail: this.errorExtensionMessage + ': <br>' + file.name
        });
      }
    }
    for (const file of this.fileUpload.files) {
      if (file.size === 0) {
        this.commonService.addToast({
          life: 5000,
          severity: 'error',
          summary: this.errorExtensionTitle,
          detail: this.errorSizeMessage + ': <br>' + file.name
        });
        this.removeFile(this.fileUpload.files.indexOf(file));
      }
    }
    // this.disableSelect = this.getNumberOfFiles() > 0 && !this.moreThanOneFile;
    this.isValid.emit(this.uploadIsValid());
    this.selectedFiles.emit(this.fileUpload.files);
  }

  /**
   * handle removing files
   * @param {number} index - index of element that should be removed
   */
  public removeFile(index: number): void {
    this.fileUpload.files.splice(index, 1);
    // this.disableSelect = this.getNumberOfFiles() > 0 && !this.moreThanOneFile;
    this.isValid.emit(this.uploadIsValid());
    this.updateIndices();
    this.selectedFiles.emit(this.fileUpload.files);
  }
 public isDisabled() {
    if (this.moreThanOneFile) {
      return false;
    }
    else {
      if (this.getNumberOfFiles() > 0) {
        return true;
      }
      return false;
    }
 }
  /**
   * change files array order
   * @param {number} index - index of array index to moveUp
   */
  public moveUpFile(index: number): void {
    if (this.moveUpIsPossible(index)) {
      const save = this.fileUpload.files[index - 1];
      this.fileUpload.files[index - 1] = this.fileUpload.files[index];
      this.fileUpload.files[index] = save;
    }
    this.updateIndices();
    this.selectedFiles.emit(this.fileUpload.files);
  }
  updateIndices(): void {
    for (let index = 0; index < this.fileUpload.files.length; index++) {
      this.fileUpload.files[index].index = index;
    }
  }
  /**
   * determine if it's possible to change array order
   * @param index - index of array index to moveUp
   */
  public moveUpIsPossible(index: number): boolean {
    return index > 0 && this.fileUpload.files.length > 1;
  }

  /**
   * change files array order
   * @param {number} index - index of array index to moveDown
   */
  public moveDownFile(index: number): void {
    if (this.moveDownIsPossible(index)) {
      const save = this.fileUpload.files[index + 1];
      this.fileUpload.files[index + 1] = this.fileUpload.files[index];
      this.fileUpload.files[index] = save;
    }
    this.updateIndices();
    this.selectedFiles.emit(this.fileUpload.files);
  }

  /**
   * determine if it's possible to change array order
   * @param index - index of array index to moveDown
   */
  public moveDownIsPossible(index: number): boolean {
    return index < this.fileUpload.files.length - 1 && this.fileUpload.files.length > 1;
  }

  /**
   * Flush all previously selected files
   */
  public clearFiles(): void {
    this.fileUpload.files = [];
    // this.disableSelect = false;
    this.isValid.emit(this.uploadIsValid());
    this.selectedFiles.emit(this.fileUpload.files);
  }

  /**
   * get the number of files already selected
   * @return {number}
   */
  public getNumberOfFiles(): number {
    return (this.fileUpload && this.fileUpload.files) ? this.fileUpload.files.length : 0;
  }

  /**
   * Determine if the whole upload is valid or not
   */
  public uploadIsValid(): boolean {
    if (this.optional) {
      return !this.tooMuchFileOnSelection() && !this.fileListContainsANotAllowedExtension();
    } else {
      return !this.tooMuchFileOnSelection() && this.getNumberOfFiles() > 0 && !this.fileListContainsANotAllowedExtension();
    }
  }

  /**
   * determine if there is too much files selected (case when the user switch of upload kind)
   * @return {boolean} - the result
   */
  public tooMuchFileOnSelection(): boolean {
    return this.moreThanOneFile ? false : this.getNumberOfFiles() > 1;
  }

  /**
   * Get translations for the component
   */
  public getTradForComponent(): void {
    this.translate.get('TOASTER.ERROR_MESSAGE_TITLE').subscribe((label: string) => {
      this.errorExtensionTitle = label;
    });
    this.translate.get('FILES.UPLOAD_COMPONENT.ERROR_MESSAGE.FILE_EXTENSION_MESSAGE').subscribe((label: string) => {
      this.errorExtensionMessage = label;
    });
    this.translate.get('FILES.UPLOAD_COMPONENT.ERROR_MESSAGE.FILE_SIZE_MESSAGE').subscribe((label: string) => {
      this.errorSizeMessage = label;
    });
  }
  onFilesChange(fileList: Array<File>): void {
  }
  onFileInvalids(fileList: Array<File>): void {
  }
  public dragStarted(dragging: boolean): void {
    this.displayDropZone = dragging;
  }
}
